import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { STATUS_STYLES, fmtDate } from "@/lib/helpers";
import { Plus, Pencil, Trash2, CreditCard, Upload } from "lucide-react";
import { IDCard } from "@/components/IDCard";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", joining_date: "", department: "Data Entry", reporting_head: "R. Aadhya", status: "Active", remarks: "", designation: "Data Entry Operator", blood_group: "", emergency_contact: "", photo: "", employee_id: "", full_time: false, salary_structure: {} };

const SALARY_FIELDS = [["basic", "Basic"], ["hra", "HRA"], ["conveyance", "Conveyance"], ["special_allowance", "Special Allow."], ["other_earnings", "Other Earnings"], ["pf", "PF"], ["professional_tax", "Prof. Tax"]];

export default function EmployeeMaster() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [idCardEmp, setIdCardEmp] = useState(null);
  const photoInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const { data: employees = [], isLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        let id = editing;
        const newId = (form.employee_id || "").trim();
        if (newId && newId !== id) {
          await api.post(`/employees/${id}/change-id`, { new_id: newId });
          id = newId;
        }
        return api.put(`/employees/${id}`, { ...form, employee_id: id });
      }
      return api.post("/employees", form);
    },
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(editing ? "Employee updated" : "Employee added");
      setOpen(false); setForm(empty); setEditing(null);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Save failed"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/employees/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employees"] }); toast.success("Employee removed"); },
  });

  const openEdit = (e) => { setEditing(e.employee_id); setForm({ ...empty, ...e }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!editing) { toast.error("Save the employee first, then add a photo"); return; }
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const res = await api.post(`/employees/${editing}/photo`, fd);
      setForm((f) => ({ ...f, photo: res.data.photo }));
      qc.invalidateQueries({ queryKey: ["employees"] });
      toast.success("Photo updated");
    } catch { toast.error("Photo upload failed"); }
    setUploading(false);
  };

  return (
    <div data-testid="employee-master">
      <PageHeader title="Employee Master" subtitle="Team directory & employee records">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-employee-btn" className="bg-navy hover:bg-navy-900 text-white rounded-lg">
              <Plus className="mr-2 h-4 w-4" /> Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-navy">{editing ? "Edit" : "Add"} Employee</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 flex items-center gap-4 rounded-lg border border-slate-200 p-3">
                <div className="h-16 w-16 shrink-0 rounded-full bg-navy/5 ring-2 ring-gold overflow-hidden flex items-center justify-center">
                  {form.photo ? <img src={form.photo} alt="photo" className="h-full w-full object-cover" /> : <span className="text-xs text-slate-400">No photo</span>}
                </div>
                <div>
                  <input ref={photoInputRef} type="file" accept="image/*" className="hidden" data-testid="emp-photo-input" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                  <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => photoInputRef.current?.click()} data-testid="emp-photo-upload-btn" className="rounded-lg">
                    <Upload className="mr-2 h-4 w-4" /> {uploading ? "Uploading…" : "Upload Photo"}
                  </Button>
                  <p className="mt-1 text-xs text-slate-400">{editing ? "JPG/PNG for the ID card" : "Save the employee first to add a photo"}</p>
                </div>
              </div>
              <div className="col-span-2"><Label>Name</Label><Input data-testid="emp-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              {editing && (
                <div className="col-span-2">
                  <Label>Employee ID</Label>
                  <Input data-testid="emp-id-input" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} />
                  <p className="mt-1 text-xs text-amber-600">Changing the ID updates it everywhere (tasks, attendance, reports).</p>
                </div>
              )}
              <div><Label>Designation</Label><Input data-testid="emp-designation-input" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
              <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
              <div><Label>Email</Label><Input data-testid="emp-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Blood Group</Label><Input data-testid="emp-blood-input" value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} placeholder="e.g. O+" /></div>
              <div><Label>Emergency Contact</Label><Input data-testid="emp-emergency-input" value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} /></div>
              <div><Label>Joining Date</Label><Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} /></div>
              <div><Label>Reporting Head</Label><Input value={form.reporting_head} onChange={(e) => setForm({ ...form, reporting_head: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="emp-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                    <SelectItem value="On Leave">On Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Remarks</Label><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
              <div className="col-span-2 rounded-lg border border-slate-200 p-3">
                <Label className="text-navy">Monthly Salary Structure</Label>
                <p className="text-xs text-slate-400 mb-2">Used to auto-fill payslips (bulk & single). Advance/Loan EMI is added automatically.</p>
                <div className="grid grid-cols-2 gap-2">
                  {SALARY_FIELDS.map(([k, l]) => (
                    <div key={k}>
                      <Label className="text-xs">{l}</Label>
                      <Input type="number" data-testid={`emp-salary-${k}`}
                        value={form.salary_structure?.[k] ?? 0}
                        onChange={(e) => setForm({ ...form, salary_structure: { ...(form.salary_structure || {}), [k]: e.target.value } })} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-span-2 flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div>
                  <Label className="text-navy">Full-Time Employee</Label>
                  <p className="text-xs text-slate-400">Full-time staff get an 8-hour daily attendance cap (part-time is 4 hours).</p>
                </div>
                <Switch data-testid="emp-fulltime-switch" checked={!!form.full_time} onCheckedChange={(v) => setForm({ ...form, full_time: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => save.mutate()} data-testid="save-employee-btn" className="bg-navy text-white">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <SectionCard testId="employee-table-card">
        {isLoading ? <Skeleton className="h-64" /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {["ID", "Name", "Email", "Phone", "Joining", "Department", "Reporting Head", "Status", ""].map((h) => (
                    <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((e) => (
                  <TableRow key={e.employee_id} data-testid={`employee-row-${e.employee_id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-navy">{e.employee_id}</TableCell>
                    <TableCell>{e.name}</TableCell>
                    <TableCell className="text-slate-500">{e.email}</TableCell>
                    <TableCell className="text-slate-500">{e.phone}</TableCell>
                    <TableCell>{fmtDate(e.joining_date)}</TableCell>
                    <TableCell>{e.department}</TableCell>
                    <TableCell>{e.reporting_head}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[e.status] || "bg-emerald-100 text-emerald-800"}>{e.status}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => setIdCardEmp(e)} data-testid={`idcard-btn-${e.employee_id}`} title="View ID Card"><CreditCard className="h-4 w-4 text-gold-dark" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`edit-${e.employee_id}`}><Pencil className="h-4 w-4 text-navy" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => del.mutate(e.employee_id)} data-testid={`delete-${e.employee_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      <Dialog open={!!idCardEmp} onOpenChange={(v) => !v && setIdCardEmp(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading text-navy">Employee ID Card</DialogTitle></DialogHeader>
          <div className="flex justify-center py-2">
            {idCardEmp && <IDCard employee={idCardEmp} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
